import { ReactNode } from 'react';

import path from 'path';

import { Box, Chip, Link as MuiLink, Typography, useTheme } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { Stack } from '@mui/system';
import { GridColDef, GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ParamKeyValuePair, Link as ReactRouterLink } from 'react-router-dom';

import LastOnline from '~components/data/lastOnline';
import InteractiveBatteryIcon from '~components/icons/interactiveBatteryIcon';
import VectorImage from '~components/media/vectorImage';
import { dateFormats, pagePathSegments } from '~constants';
import KeyTypeEnum, { getKeyTypeLabel } from '~enums/keyTypeEnum';
import { GeoCoordinate } from '~interfaces';
import DeviceModel from '~interfaces/deviceModel';
import i18n from '~lib/i18n';
import { Dimens } from '~themes';
import { defaultPlaceholder, ifNullOrEmpty } from '~utils/stringUtils';

import { ColDef } from './interfaces/colDef';
import { GridColTypeEnum } from './interfaces/gridColTypeEnum';
import RowActionsCell, { type RowAction } from './rowActionsCell';

export interface GridColConfig<T extends GridValidRowModel = GridValidRowModel> {
	type?: GridColTypeEnum;
	linkPath?: string;
	linkState?: object;
	addIdToPath?: boolean;
	linkPathIdGetter?: (row: T) => string;
	pathQueryParametersGetter?: (value: T) => ParamKeyValuePair[];
	valueGetter?: (value: T) => string | Dayjs;
	valueFormatter?: (value: T) => string;
	rowActions?: RowAction[] | ((params: GridRenderCellParams<T>) => RowAction[]);
}

const getCell = ({
	theme,
	value,
	linkPathId: rowId,
	gridColType,
	linkPath,
	addIdToPath,
	linkState,
	pathQueryParameters,
	rowActions,
}: {
	theme: Theme;
	value: string | number | Dayjs | GeoCoordinate | ReactNode;
	gridColType?: GridColTypeEnum;
	linkPath?: string;
	linkPathId?: string;
	addIdToPath?: boolean;
	linkState?: object;
	pathQueryParameters?: ParamKeyValuePair[];
	rowActions?: RowAction[];
}) => {
	const { lineHeight, ...labelNoLineHeight } = theme.typography.label;

	let to = linkPath ?? '';
	if (addIdToPath && rowId) {
		if (to.includes(':id')) {
			to = to.replace(':id', rowId);
		} else {
			to = path.join(to, rowId);
		}
	}

	if (pathQueryParameters) {
		for (let i = 0; i < pathQueryParameters.length; i++) {
			const param = pathQueryParameters[i];
			to += i == 0 ? '?' : '&';
			to += `${param[0]}=${param[1]}`;
		}
	}

	const stringValue = ifNullOrEmpty(value as string);
	const numberValue = value as number;
	const dayJsValue = value as Dayjs;
	const gpsValue = value as GeoCoordinate;
	const nodeValue = value as ReactNode;
	const booleanValue = value as boolean;

	switch (gridColType) {
		case GridColTypeEnum.Link: {
			return (
				<MuiLink
					component={ReactRouterLink}
					to={`/${to}`}
					state={linkState}
					sx={{
						...labelNoLineHeight,
						color: theme.palette.textPrimary.main,
						'&:hover': { textDecoration: 'underline' },
						textDecoration: 'none',
					}}
				>
					{stringValue}
				</MuiLink>
			);
		}
		case GridColTypeEnum.Chip: {
			const chipValue = typeof value === 'string' ? value : String(value ?? '');
			return <Chip key={stringValue} label={chipValue.toLowerCase()} />;
		}
		case GridColTypeEnum.Battery:
			return (
				<Box display='flex' sx={{ height: 1, alignItems: 'center', gap: Dimens.spacingXs }}>
					{numberValue > 0 && <InteractiveBatteryIcon value={numberValue} />}
					<Typography variant='labelSm'>{numberValue ? `${numberValue}%` : '-'}</Typography>
				</Box>
			);
		case GridColTypeEnum.Dayjs:
			return (
				<Typography variant='paragraph'>
					{dayJsValue?.format(dateFormats.dateTime) ?? defaultPlaceholder}
				</Typography>
			);
		case GridColTypeEnum.Location:
			return (
				<Stack direction='row' alignItems='center' sx={{ height: '100%' }} gap={Dimens.spacingXs}>
					{typeof gpsValue === 'object' ?
						<>
							<VectorImage name='icon/location' size={16} />
							<MuiLink
								href={`https://www.google.com/maps/place/${gpsValue.latitude},${gpsValue.longitude}`}
								target='_blank'
								rel='noreferrer'
							>
								{gpsValue.latitude}, {gpsValue.longitude}
							</MuiLink>
						</>
					:	<Typography variant='labelSm'>-</Typography>}
				</Stack>
			);
		case GridColTypeEnum.RowActions: {
			return <RowActionsCell actions={rowActions ?? []} buttonAriaLabel='Action buttons' />;
		}
		case GridColTypeEnum.LastOnline:
			return (
				<Box sx={{ height: '100%' }} alignContent={'center'}>
					<LastOnline date={dayJsValue} />
				</Box>
			);
		case GridColTypeEnum.TextBox:
			return (
				<Box sx={{ width: '100%', minWidth: 0 }}>
					<Typography
						variant='paragraph'
						sx={{
							display: 'block',
							width: '100%',
							whiteSpace: 'normal',
							wordBreak: 'break-word',
							overflowWrap: 'anywhere',
						}}
					>
						{nodeValue}
					</Typography>
				</Box>
			);
		case GridColTypeEnum.Regular:
		default:
			return (
				<Typography variant='paragraph'>
					{typeof value === 'number' ?
						numberValue
					: typeof value === 'boolean' ?
						booleanValue ?
							'true'
						:	'false'
					:	stringValue}
				</Typography>
			);
	}
};

const useColDefs = <T extends GridValidRowModel = GridValidRowModel>() => {
	const { t } = useTranslation();
	const theme = useTheme();

	const colDefaults = {
		sortable: false,
		hideable: false,
		filterable: false,
		disableColumnMenu: true,
		flex: 1,
	};

	return {
		// Generic col defs (defined in models that dictate the data grid type.)
		gridColDefFor: (
			colDef: ColDef<T>,
			pathQueryParametersGetter?: (value: T) => ParamKeyValuePair[],
			linkState?: object
		): GridColDef => {
			return {
				...colDefaults,
				field: colDef.field,

				headerName: t(colDef.headerName),
				minWidth: colDef.minWidth,
				maxWidth: colDef.maxWidth,
				renderCell: (params) =>
					getCell({
						theme: theme,
						value: colDef.valueGetter?.(params.row) ?? params.value,
						linkPathId: colDef.linkPathIdGetter?.(params.row) ?? String(params.id),
						gridColType: colDef.gridColType,
						addIdToPath: colDef.addIdToPath,
						linkPath: colDef.linkPathGetter?.(params.row) ?? '',
						pathQueryParameters: pathQueryParametersGetter?.(params.row),
						linkState: linkState,
					}),
			};
		},
		// Reusable col defs (can be reused with minimal configuration to ensure consistency across datagrids.)
		// Note: These we'll have to be split up to colDef<T>s. Otherwise it's going to be very difficult to
		// use with the toggleable columns.
		deviceModel: (): GridColDef => {
			return {
				field: 'model',
				valueGetter: (value: DeviceModel) => value.name,
				headerName: t('device.fields.model', { ns: 'entities' }),
				renderCell: (params) =>
					getCell({
						theme: theme,
						value: params.value,
						gridColType: GridColTypeEnum.Regular,
					}),
				...colDefaults,
			};
		},
		endDate: (valueGetter?: (value: T) => Dayjs | undefined): GridColDef => {
			return {
				...colDefaults,
				field: 'end',
				headerName: t('end', { ns: 'date' }),
				renderCell: (params) =>
					getCell({
						theme: theme,
						value: valueGetter?.(params.row),
						gridColType: GridColTypeEnum.Dayjs,
					}),
			};
		},
		hardwareId: (
			pathQueryParametersGetter?: (value: T) => ParamKeyValuePair[],
			isLink: boolean = true,
			linkPathIdGetter?: (value: T) => string,
			valueGetter?: (value: T) => string
		): GridColDef => {
			return {
				...colDefaults,
				field: 'hardwareId',
				headerName: t('device.fields.hardwareUid', { ns: 'entities' }),
				renderCell: (params) =>
					getCell({
						theme: theme,
						value: valueGetter?.(params.row) ?? params.value,
						linkPathId: linkPathIdGetter?.(params.row) ?? params.id.toString(),
						addIdToPath: isLink,
						gridColType: isLink ? GridColTypeEnum.Link : GridColTypeEnum.Regular,
						linkPath: path.join(pagePathSegments.Devices, ':id', pagePathSegments.General),
						pathQueryParameters: pathQueryParametersGetter?.(params.row),
					}),
			};
		},
		id: (
			linkPath?: string,
			pathQueryParametersGetter?: (value: T) => ParamKeyValuePair[],
			isLink: boolean = true,
			linkState?: object
		): GridColDef => {
			return {
				...colDefaults,
				field: 'id',
				headerName: t('general.id', { ns: 'entities' }),
				minWidth: 75,
				maxWidth: 75,
				renderCell: (params) =>
					getCell({
						theme: theme,
						value: params.value,
						linkPathId: params.id.toString(),
						gridColType: isLink ? GridColTypeEnum.Link : GridColTypeEnum.Regular,
						addIdToPath: isLink,
						linkPath: linkPath,
						linkState: linkState,
						pathQueryParameters: pathQueryParametersGetter?.(params.row),
					}),
			};
		},
		keyType: (valueGetter: (value: T) => KeyTypeEnum | null): GridColDef => {
			return {
				...colDefaults,
				field: 'type',
				headerName: t('general.type', { ns: 'entities' }),
				renderCell: (params) => {
					const keyType = valueGetter(params.row);
					let value = '';
					if (keyType == null) {
						value = i18n.t('general.unknown', { ns: 'entities' });
					} else {
						value = getKeyTypeLabel(keyType, i18n.t);
					}
					return getCell({
						theme: theme,
						value: value,
						linkPathId: params.id.toString(),
						gridColType: GridColTypeEnum.Regular,
					});
				},
			};
		},
		name: (
			isLink: boolean = false,
			linkPath?: string,
			pathQueryParametersGetter?: (value: T) => ParamKeyValuePair[],
			linkPathIdGetter?: (value: T) => string,
			linkState?: object,
			valueGetter?: (value: T) => string
		): GridColDef => {
			return {
				...colDefaults,
				field: 'name',
				headerName: t('general.name', { ns: 'entities' }),
				renderCell: (params) =>
					getCell({
						theme: theme,
						value: valueGetter?.(params.row) ?? params.value,
						linkPathId: linkPathIdGetter?.(params.row) ?? params.id.toString(),
						gridColType: isLink ? GridColTypeEnum.Link : GridColTypeEnum.Regular,
						addIdToPath: isLink,
						linkPath: linkPath,
						linkState: linkState,
						pathQueryParameters: pathQueryParametersGetter?.(params.row),
					}),
			};
		},
		organisationName: (
			isLink: boolean = false,
			linkPathIdGetter: (value: T) => string
		): GridColDef => {
			return {
				field: 'organisationName',
				headerName: t('organisation.labels.single', { ns: 'entities' }),
				renderCell: (params) =>
					getCell({
						theme: theme,
						value: params.value,
						gridColType: isLink ? GridColTypeEnum.Link : GridColTypeEnum.Regular,
						linkPath: path.join(pagePathSegments.Organisations, ':id', pagePathSegments.General),
						addIdToPath: isLink,
						linkPathId: linkPathIdGetter(params.row),
					}),
				...colDefaults,
			};
		},
		resourceGroup: (valueGetter?: (value: T) => string | undefined): GridColDef => {
			return {
				field: 'resourceGroupName',
				headerName: t('resourceGroup.labels.single', { ns: 'entities' }),
				renderCell: (params) =>
					getCell({
						theme: theme,
						value: valueGetter?.(params.row) ?? params.value ?? '',
						gridColType: GridColTypeEnum.Regular,
					}),
				...colDefaults,
			};
		},
		rowActions: (getRowActions: (row: T) => RowAction[]): GridColDef => {
			return {
				...colDefaults,
				field: 'actions',
				headerName: '',
				type: 'actions',
				align: 'right',
				headerAlign: 'right',
				maxWidth: 80,
				minWidth: 80,
				renderCell: (params) =>
					getCell({
						theme: theme,
						value: '',
						gridColType: GridColTypeEnum.RowActions,
						rowActions: getRowActions(params.row),
					}),
			};
		},
		serialNumber: (
			valueGetter?: (value: T) => string,
			linkPathIdGetter?: (value: T) => string,
			pathQueryParametersGetter?: (value: T) => ParamKeyValuePair[],
			isLink: boolean = false
		): GridColDef => {
			return {
				...colDefaults,
				field: 'serialNumber',
				headerName: t('key.fields.serialNumber', { ns: 'entities' }),
				renderCell: (params) =>
					getCell({
						theme: theme,
						value: valueGetter?.(params.row) ?? params.value,
						linkPath: path.join(pagePathSegments.Keys, ':id', pagePathSegments.General),
						gridColType: isLink ? GridColTypeEnum.Link : GridColTypeEnum.Regular,
						addIdToPath: isLink,
						linkPathId: linkPathIdGetter?.(params.row),
						pathQueryParameters: pathQueryParametersGetter?.(params.row),
					}),
			};
		},
		startDate: (valueGetter?: (value: T) => Dayjs | undefined): GridColDef => {
			return {
				...colDefaults,
				field: 'start',
				headerName: t('start', { ns: 'date' }),
				renderCell: (params) =>
					getCell({
						theme: theme,
						value: valueGetter?.(params.row),
						gridColType: GridColTypeEnum.Dayjs,
					}),
			};
		},
	};
};

export default useColDefs;

export type { RowAction };
